import React from 'react';
import { parseApiDate } from '../../lib/format';

export type TimelineVariant = 'primary' | 'navy';

export type TimelineChipTone = 'success' | 'warning' | 'danger' | 'muted';

export interface TimelineFiscalChip {
  label: string;
  tone?: TimelineChipTone;
}

export interface TimelineItem {
  id: string | number;
  date: string | Date;
  fiscalChip?: TimelineFiscalChip;
}

export interface TimelineProps<T extends TimelineItem> {
  items: T[];
  variant?: TimelineVariant;
  renderItem: (item: T, state: { isLatest: boolean }) => React.ReactNode;
  latestLabel?: string;
  className?: string;
}

const VARIANT_ACCENT: Record<TimelineVariant, string> = {
  primary: 'var(--ec-primary)',
  navy: 'var(--ec-navy)',
};

const CHIP_TONE_COLOR: Record<TimelineChipTone, string> = {
  success: 'var(--ec-success)',
  warning: 'var(--ec-warning)',
  danger: 'var(--ec-danger)',
  muted: 'var(--ec-text-muted)',
};

function findLatestId<T extends TimelineItem>(items: T[]): T['id'] | null {
  let latestId: T['id'] | null = null;
  let latestTime = -Infinity;
  items.forEach((item) => {
    const parsed = parseApiDate(item.date);
    const time = parsed ? parsed.getTime() : NaN;
    if (!Number.isNaN(time) && time > latestTime) {
      latestTime = time;
      latestId = item.id;
    }
  });
  return latestId;
}

function Timeline<T extends TimelineItem>({
  items,
  variant = 'primary',
  renderItem,
  latestLabel = 'ÚLTIMA',
  className = '',
}: TimelineProps<T>) {
  const accent = VARIANT_ACCENT[variant];
  const latestId = findLatestId(items);

  return (
    <div className={className}>
      {items.map((item, idx) => {
        const date = parseApiDate(item.date);
        const isLatest = item.id === latestId;
        const chipColor = item.fiscalChip ? CHIP_TONE_COLOR[item.fiscalChip.tone || 'muted'] : undefined;
        return (
          <div
            key={item.id}
            style={{
              display: 'flex',
              gap: 16,
              marginBottom: 20,
              paddingBottom: 20,
              borderBottom: idx < items.length - 1 ? '1px solid var(--ec-border-light)' : 'none',
            }}
          >
            <div style={{ minWidth: 80, textAlign: 'center' }}>
              <div
                style={{
                  background: isLatest ? accent : 'var(--ec-surface-sunken)',
                  color: isLatest ? '#fff' : 'var(--ec-text-secondary)',
                  borderRadius: 10,
                  padding: '8px 6px',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                <div style={{ fontSize: 20, fontWeight: 700 }}>{date ? date.getDate() : '-'}</div>
                <div>{date ? date.toLocaleDateString('es-DO', { month: 'short' }).toUpperCase() : ''}</div>
                <div style={{ fontSize: 11, opacity: 0.8 }}>{date ? date.getFullYear() : ''}</div>
              </div>
              {isLatest && (
                <div style={{ fontSize: 10, color: accent, fontWeight: 600, marginTop: 4 }}>
                  {latestLabel}
                </div>
              )}
              {item.fiscalChip && (
                <div
                  style={{
                    display: 'inline-block',
                    marginTop: 6,
                    fontSize: 10,
                    fontWeight: 600,
                    padding: '2px 6px',
                    borderRadius: 20,
                    color: chipColor,
                    border: `1px solid ${chipColor}`,
                  }}
                >
                  {item.fiscalChip.label}
                </div>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>{renderItem(item, { isLatest })}</div>
          </div>
        );
      })}
    </div>
  );
}

export default Timeline;
